import { collabStateManager } from '@databases/collab-state-manager';
import { viewState } from '@databases/database-state';
import { appStore } from '@databases/redux/store';
import { container } from '@databases/service-container';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';
import { createCurrentViewIdentityReader } from '../bootstrap/get-current-view-layout-type.ts';
import { createTableV1SelectionDependencies } from './create-table-v1-selection-dependencies.ts';
import { createTableV2LiveSource } from './create-table-v2-live-source.ts';
import { createTableV2ViewLifecycle } from './create-table-v2-view-lifecycle.ts';
import { createTableV2ViewSession } from './create-table-v2-view-session.ts';

/**
 * Shared app/preview wiring: resolve the selected layout, then route its selection
 * dependencies. Using one identity reader keeps rendering, highlights and
 * selections in agreement when persisted layout and view-state notifications
 * arrive in either order.
 *
 * Construction only creates handles. The coordinator later calls tableV2Lifecycle
 * after SDK setup and before highlights/selections; only a selected V2 view then
 * captures data. Other layouts keep their existing selection factory. React gets
 * the passive tableV2Host handle, not control over source setup or teardown.
 */
export function createApplicationLayoutComposition() {
  const readIdentity = createCurrentViewIdentityReader({
    getView: () => viewState.view,
    getDatabase: (contentId) =>
      collabStateManager.getCollabManager(contentId)
        ? {
            sdk: container.resolve('DatabaseSdk'),
            registry: container.resolve('ViewLayoutRegistry'),
          }
        : undefined,
  });

  const tableV2 = createTableV2ViewLifecycle((contentId) => {
    if (readIdentity(contentId).layoutType !== TABLE_V2_LAYOUT_ID) {
      return undefined;
    }

    return createTableV2ViewSession({
      contentId,
      source: createTableV2LiveSource(contentId),
      subscribePermissions: (listener) =>
        container.resolve('PermissionsManager').subscribe(listener),
      getSpaceKey: () => {
        const database = selectDatabaseByContentIdOptional(appStore.getState(), {
          contentId,
        });

        return database && !isBrokenDatabase(database) ? database.space.key : '';
      },
    });
  });

  return {
    tableV2Lifecycle: tableV2.lifecycle,
    tableV2Host: tableV2.host,
    readIdentity,
    getPresenceDependencies(contentId: string) {
      return readIdentity(contentId).layoutType === TABLE_V2_LAYOUT_ID
        ? tableV2.getPresenceDependencies()
        : undefined;
    },
    getSelectionDependencies(contentId: string) {
      const { layoutType } = readIdentity(contentId);

      // Missing V2 data must never select a different layout's coordinate adapter.
      return layoutType === TABLE_V2_LAYOUT_ID
        ? tableV2.getSelectionDependencies()
        : createTableV1SelectionDependencies(contentId, layoutType);
    },
  };
}
